import { GQL } from "@binale-tech/shared";

import { DmsType } from "@domain/models/dms";

import { withThinking } from "../runner/runAgent";
import type { AgentStep, ReportLine } from "../runner/types";
import { isInYear } from "./inYear";
import { MAX_IDS_PER_JOB } from "./draftSelection";
import { sendInChunks } from "./paging";

export const kasseFromDocumentsStep: AgentStep = {
    id: "kasseFromDocuments",
    product: [GQL.IProductKey.CashRegister],
    async run(ctx, io) {
        // documents that already have a Kasse transaction are not sent again
        const linked = new Set(ctx.cashRegisterTransactions().flatMap((tx) => tx.documents.map((d) => String(d.id))));
        const documents = [...ctx.documents().values()].filter(
            (doc) => doc.type === DmsType.KB && isInYear(doc.documentDate, ctx.year) && !linked.has(String(doc.key)),
        );
        const work = ctx
            .cashRegisterFolders()
            .map((folder) => ({
                ...folder,
                ids: documents.filter((doc) => doc.subType === folder.subTypeId).map((doc) => String(doc.key)),
            }))
            .filter((folder) => folder.ids.length > 0);
        if (work.length === 0) return { status: "nothing" };

        let created = 0;
        let refused = 0;
        for (const folder of work) {
            if (io.isCancelled()) break;
            const results = await withThinking(io, "app.agent.kasse.thinking", () =>
                sendInChunks(folder.ids, MAX_IDS_PER_JOB, (chunk) =>
                    ctx.createKasseFromDocuments(folder.cashRegisterId, chunk),
                ),
            );
            const count = (outcome: GQL.ICashRegisterFromDocumentOutcome) =>
                results.filter((r) => r.outcome === outcome).length;
            const made = count(GQL.ICashRegisterFromDocumentOutcome.Created);
            created += made;
            refused += count(GQL.ICashRegisterFromDocumentOutcome.Refused);
            if (made > 0) {
                io.post({
                    kind: "done",
                    textId: "app.agent.kasse.done",
                    values: { count: made, register: folder.name },
                });
            }
        }
        if (created === 0 && refused === 0) return { status: "nothing" };

        const open: ReportLine[] =
            refused > 0 ? [{ textId: "app.agent.report.kasseRefused", values: { count: refused } }] : [];
        const summary: ReportLine[] =
            created > 0 ? [{ textId: "app.agent.report.kasseCreated", values: { count: created } }] : [];
        return { status: "done", summary, open };
    },
};
